import { useState, useEffect, useRef } from 'react'
import { Medal, Plus, Trash, FloppyDisk, Warning, Buildings, FileCsv, FilePdf } from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { downloadCSV, openPrintWindow } from '../esg/exportHelpers'

// ─── Style constants ───────────────────────────────────────────────────────────
const CARD = {
  background: '#FFFFFF', borderRadius: '8px', padding: '1.5rem',
  boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: '1px solid #DDD5C8',
}
const LABEL_STYLE = { fontSize: '0.75rem', fontWeight: '600', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem', display: 'block' }
const INPUT_STYLE = { width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #DDD5C8', borderRadius: '6px', fontSize: '0.875rem', color: '#2C2017', boxSizing: 'border-box', outline: 'none' }
const BTN_PRIMARY = { padding: '0.5rem 1rem', background: '#5C4033', color: '#FFF', border: 'none', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }
const BTN_GHOST = { padding: '0.4rem 0.75rem', background: 'none', color: '#7A6A5A', border: '1px solid #DDD5C8', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer' }

// ─── GRESB Management structure (GRESB Real Estate Scoring Document 2025) ────
// Fuente: GRESB Real Estate Scoring Document 2025 — Management = 30 pts
// Tipo G = General indicator | Tipo S = Social indicator
// LE1, RP2_2 y RM7 no puntúan (solo reporting)
const MGMT_SECTIONS = [
  { id: 'LE', label: 'LE — Liderazgo', maxPts: 7, color: '#4A7C59', items: [
    { id: 'LE2',  label: 'Individuo responsable de los objetivos ESG a nivel de organización', pts: 1,    tipo: 'G' },
    { id: 'LE3',  label: 'Objetivos ESG, climáticos y/o de capital humano cuantificados con plazos',     pts: 2,    tipo: 'G' },
    { id: 'LE4',  label: 'Grupo de trabajo / comité ESG',                                                pts: 1,    tipo: 'G' },
    { id: 'LE5',  label: 'Decisor sénior en ESG y clima (junta directiva o comité ejecutivo)',           pts: 1,    tipo: 'G' },
    { id: 'LE6',  label: 'Objetivos de rendimiento ESG vinculados a la remuneración del personal',      pts: 2,    tipo: 'G' },
  ]},
  { id: 'PO', label: 'PO — Políticas', maxPts: 4.5, color: '#2C6E91', items: [
    { id: 'PO1',  label: 'Política medioambiental documentada y vigente',                               pts: 1.5,  tipo: 'G' },
    { id: 'PO2',  label: 'Política social documentada y vigente',                                       pts: 1.5,  tipo: 'G' },
    { id: 'PO3',  label: 'Código de gobernanza / política ESG global publicado',                        pts: 1.5,  tipo: 'G' },
  ]},
  { id: 'RP', label: 'RP — Reporting', maxPts: 3.75, color: '#7A4E2D', items: [
    { id: 'RP1',  label: 'Reporte de sostenibilidad publicado (GRI, EPRA, TCFD/ISSB, SFDR, etc.)',      pts: 3.5,  tipo: 'G' },
    { id: 'RP2_1',label: 'Aseguramiento externo de datos de sostenibilidad (auditoría independiente)',  pts: 0.25, tipo: 'G' },
  ]},
  { id: 'RM', label: 'RM — Gestión de Riesgos', maxPts: 4.75, color: '#8B5E3C', items: [
    { id: 'RM1',  label: 'Proceso integrado de gestión de riesgos ESG a nivel de portfolio',            pts: 1.25, tipo: 'G' },
    { id: 'RM2',  label: 'Identificación de riesgos y oportunidades relacionados con el clima',         pts: 0.25, tipo: 'G' },
    { id: 'RM3_1',label: 'Evaluación de riesgos físicos climáticos por activo (inundaciones, calor…)', pts: 0.25, tipo: 'S' },
    { id: 'RM3_2',label: 'Evaluación de riesgos de transición climática del portfolio',                 pts: 0.25, tipo: 'G' },
    { id: 'RM4',  label: 'Plan de acción documentado para riesgos climáticos identificados',            pts: 0.25, tipo: 'G' },
    { id: 'RM5',  label: 'Evaluación de resiliencia climática del portfolio (escenarios 1.5°C/2°C)',    pts: 0.5,  tipo: 'G' },
    { id: 'RM6_1',label: 'Identificación de riesgos de transición (regulatorios, tecnológicos, mercado)', pts: 0.5, tipo: 'G' },
    { id: 'RM6_2',label: 'Evaluación del impacto financiero de riesgos de transición',                 pts: 0.5,  tipo: 'G' },
    { id: 'RM6_3',label: 'Divulgación pública de riesgos de transición (TCFD / ISSB)',                  pts: 0.5,  tipo: 'G' },
    { id: 'RM6_4',label: 'Integración de riesgos de transición en la estrategia de inversión',          pts: 0.5,  tipo: 'G' },
  ]},
  { id: 'SE', label: 'SE — Stakeholder Engagement', maxPts: 10, color: '#5C4088', items: [
    { id: 'SE1',  label: 'Programa estructurado de engagement con inquilinos/ocupantes',                pts: 1,    tipo: 'S' },
    { id: 'SE2_1',label: 'Programa de engagement con la comunidad local',                               pts: 1,    tipo: 'S' },
    { id: 'SE2_2',label: 'Iniciativas comunitarias documentadas con impacto medible',                   pts: 1,    tipo: 'S' },
    { id: 'SE3_1',label: 'Programa de salud y seguridad laboral para empleados',                        pts: 0.75, tipo: 'S' },
    { id: 'SE3_2',label: 'Programa de salud y bienestar para empleados y/u ocupantes del edificio',     pts: 1.25, tipo: 'S' },
    { id: 'SE4',  label: 'Programa de desarrollo profesional y formación del personal',                 pts: 0.5,  tipo: 'S' },
    { id: 'SE5',  label: 'Programa de diversidad, equidad e inclusión (DEI)',                           pts: 0.5,  tipo: 'S' },
    { id: 'SE6',  label: 'Programa de engagement y criterios ESG en cadena de suministro',              pts: 1.5,  tipo: 'S' },
    { id: 'SE7_1',label: 'Proceso activo de engagement con stakeholders clave',                         pts: 1,    tipo: 'S' },
    { id: 'SE7_2',label: 'Satisfacción de stakeholders medida y reportada',                             pts: 1,    tipo: 'S' },
    { id: 'SE8',  label: 'Proceso formal de quejas y reclamaciones para stakeholders',                  pts: 0.5,  tipo: 'S' },
  ]},
]

const CERT_ENV_OPTS = ['Ninguna', 'BREEAM Pass', 'BREEAM Good', 'BREEAM Very Good', 'BREEAM Excellent', 'BREEAM Outstanding', 'LEED Certified', 'LEED Silver', 'LEED Gold', 'LEED Platinum', 'DGNB Bronze', 'DGNB Silver', 'DGNB Gold', 'DGNB Platinum', 'Otra']
const CERT_HLW_OPTS = ['Ninguna', 'WELL Bronze', 'WELL Silver', 'WELL Gold', 'WELL Platinum', 'Fitwel 1 estrella', 'Fitwel 2 estrellas', 'Fitwel 3 estrellas', 'Otra']
const USO_OPTS = ['Oficinas', 'Retail', 'Industrial/Logístico', 'Residencial', 'Hotel', 'Mixto', 'Otro']

// ─── GRESB Validation Status ───────────────────────────────────────────────────
// Asignado por GRESB tras revisar la certificación. Si no se sabe, usar 'full'.
const VALIDATION_OPTS = [
  { value: 'full',          label: 'Full points (×1.0) — cert. reconocida',  weight: 1.0 },
  { value: 'partial_plus',  label: 'Partial+ (×0.6) — cert. parcialmente reconocida', weight: 0.6 },
  { value: 'partial_minus', label: 'Partial- (×0.3) — cert. reconocida con reservas',  weight: 0.3 },
]
const VALIDATION_WEIGHT = { full: 1.0, partial_plus: 0.6, partial_minus: 0.3 }

// ─── Factor tiempo BC1.1 (Diseño/Construcción) ────────────────────────────────
// Fuente: GRESB Real Estate Scoring Document 2025, tabla pág. 105
function bc11TimeFactor(yearStr) {
  if (!yearStr) return 1.0
  const age = new Date().getFullYear() - parseInt(yearStr)
  if (age <= 3)  return 1.00
  if (age === 4) return 0.90
  if (age === 5) return 0.80
  if (age === 6) return 0.74
  if (age === 7) return 0.67
  if (age === 8) return 0.61
  if (age === 9) return 0.54
  if (age === 10) return 0.48
  if (age === 11) return 0.40
  if (age === 12) return 0.32
  if (age === 13) return 0.24
  if (age === 14) return 0.16
  return 0.08  // ≥15 años: factor mínimo permanente
}

// ─── Factor tiempo BC1.2 (Operacional) ───────────────────────────────────────
// Fuente: GRESB Real Estate Scoring Document 2025, tabla pág. 106
// La certificación operacional caduca a los 5 años → factor 0%
function bc12TimeFactor(yearStr) {
  if (!yearStr) return 1.0
  const age = new Date().getFullYear() - parseInt(yearStr)
  if (age <= 3)  return 1.00
  if (age === 4) return 0.50
  return 0  // caducada (≥5 años)
}

// ─── Scoring helpers ───────────────────────────────────────────────────────────
// BC1.1: % m² ajustado (×validación ×factor tiempo) → pts estimados (max 7 pts)
// Fuente: GRESB Real Estate Scoring Document 2025 — BC1.1 es scoring RELATIVO al peer group.
// Esta estimación lineal es conservadora; el score real depende del benchmark GRESB.
// El NIVEL de certificación (Very Good, Excellent...) NO afecta al score GRESB (solo reporting).
function bc11Pts(adjPct) {
  return Math.min(Math.round((adjPct / 100) * 7 * 10) / 10, 7)
}
// BC1.2: % m² ajustado → pts estimados (max 8.5 pts)
// Fuente: GRESB Scoring Doc 2025 — BC1.1 + BC1.2 tienen CAP CONJUNTO de 8.5 pts
function bc12Pts(adjPct) {
  return Math.min(Math.round((adjPct / 100) * 8.5 * 10) / 10, 8.5)
}

// EN1, GH1, WT1, WS1: scoring RELATIVO al peer group GRESB — no pre-calculable sin benchmark
// Fuente: GRESB Real Estate Scoring Document 2025
// La puntuación la asigna GRESB tras comparar con el benchmark del peer group.
// Esta herramienta registra los datos medidos para el volcado al cuestionario oficial.
const PERF_RELATIVE = {
  EN1: { max: 14, unit: 'kWh/m²·año',    label: 'EN1 Intensidad Energética' },
  GH1: { max: 14, unit: 'kgCO₂e/m²·año', label: 'GH1 Intensidad GEI' },
  WT1: { max: 8,  unit: 'm³/m²·año',      label: 'WT1 Intensidad Agua' },
  WS1: { max: 5,  unit: '%',              label: 'WS1 Desvío de Vertedero' },
}

function newAsset(id) {
  return {
    id, nombre: '', uso: 'Oficinas', m2: '',
    kwh_year: '', kwh_from_ene1: false,
    tco2_scope1: '', tco2_scope2: '', m3_year: '',
    cert_env: 'Ninguna', cert_env_year: '', cert_env_validation: 'full',
    cert_hlw: 'Ninguna', cert_hlw_year: '', cert_hlw_validation: 'full',
  }
}

function defaultData() {
  const management = {}
  MGMT_SECTIONS.forEach(s => {
    management[s.id] = { items: Object.fromEntries(s.items.map(i => [i.id, 'no'])), notes: '' }
  })
  return { management, performance: { assets: [], ws_total_t: '', ws_pct_desvio: '' } }
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function GRESBAssessment({ project }) {
  const storageKey = `gresb_data_${project?.id || 'default'}`
  const [activeTab, setActiveTab] = useState('dashboard')
  const [data, setData] = useState(defaultData)
  const [savedMsg, setSavedMsg] = useState(false)
  const [openSection, setOpenSection] = useState('LE')
  const [editAssetId, setEditAssetId] = useState(null)
  const initialized = useRef(false)

  const [saveError, setSaveError] = useState(null)
  const [sensors,   setSensors]   = useState(null)
  const [fallbackM2, setFallbackM2] = useState(null)
  const [iros,      setIros]      = useState([])
  const [iroSettings, setIroSettings] = useState(null)
  const [autofillMsg, setAutofillMsg] = useState(null)

  // Carga inicial: último snapshot framework='gresb' en esg_snapshots + sensores agregados
  useEffect(() => {
    let cancel = false
    async function load() {
      if (!project?.id) { initialized.current = true; return }
      // 1) Snapshot GRESB
      const { data: snaps } = await supabase
        .from('esg_snapshots').select('source_data, created_at')
        .eq('project_id', project.id).eq('framework', 'gresb')
        .order('created_at', { ascending: false }).limit(1)
      if (cancel) return
      const snap = snaps?.[0]?.source_data
      if (snap && (snap.management || snap.performance)) {
        setData(snap)
      } else {
        // Fallback: legacy localStorage
        const raw = localStorage.getItem(storageKey)
        if (raw) { try { setData(JSON.parse(raw)) } catch {} }
      }
      initialized.current = true
      // 2a) Cargar ENE 1 para extraer m² (fallback si portfolio sin assets)
      const { data: ene1Row } = await supabase
        .from('evaluations').select('justificacion')
        .eq('project_id', project.id).eq('criteria_code', 'ENE 1').maybeSingle()
      if (ene1Row?.justificacion && !cancel) {
        try {
          const j = JSON.parse(ene1Row.justificacion)
          const zonas = j?.ene1_calculadora?.zonas
          if (Array.isArray(zonas)) {
            const m2sum = zonas.filter(z => parseFloat(z.m2) > 0).reduce((s, z) => s + parseFloat(z.m2), 0)
            if (m2sum > 0) setFallbackM2(m2sum)
          }
        } catch {}
      }
      // 2b) Sensores agregados (último año) — para alimentar EN1/GH1/WT1 cuando faltan en assets
      const { data: rows } = await supabase
        .from('building_sensors').select('*').eq('project_id', project.id)
        .order('reading_year', { ascending: false })
      if (rows?.length && !cancel) {
        const yMap = new Map()
        rows.forEach(r => {
          const a = yMap.get(r.reading_year) || { year: r.reading_year, kwh: 0, electricity_kwh: 0, gas_kwh: 0, co2: 0, m3: 0, n: 0 }
          // Suma total = energy_kwh_total si está, sino componentes
          const totalThisMonth = +(r.energy_kwh_total || 0) || (
            +(r.electricity_kwh || 0) + +(r.gas_kwh || 0) + +(r.district_heat_kwh || 0) + +(r.energy_kwh_renewable || 0)
          )
          a.kwh += totalThisMonth
          a.electricity_kwh += +(r.electricity_kwh || 0)
          a.gas_kwh += +(r.gas_kwh || 0)
          a.co2 += +(r.scope1_kgco2e || 0) + +(r.scope2_kgco2e || 0)
          a.m3  += +(r.water_m3_total || 0)
          a.n += 1
          yMap.set(r.reading_year, a)
        })
        const years = [...yMap.keys()].sort((a, b) => b - a)
        const y = yMap.get(years[0])
        // Si co2 no se cargó pero hay electricidad, derivar Scope 2 (factor REE 0,181 kgCO2/kWh)
        if (y.co2 === 0 && y.electricity_kwh > 0) {
          y.co2 = y.electricity_kwh * 0.181
          y.co2_derived = true
        }
        // Scope 1 desde gas si está
        if (y.gas_kwh > 0) y.co2 += y.gas_kwh * 0.20
        setSensors(y)
      }
      // 2c) IROs operativos + settings — para auto-cumplir preguntas RM
      const [{ data: iroRows }, { data: iroSet }] = await Promise.all([
        supabase.from('operational_iros').select('*').eq('project_id', project.id).neq('status', 'dismissed').neq('status', 'closed'),
        supabase.from('operational_iro_settings').select('*').eq('project_id', project.id).maybeSingle(),
      ])
      if (!cancel) {
        setIros(iroRows || [])
        setIroSettings(iroSet || null)
      }
    }
    load()
    return () => { cancel = true }
  // eslint-disable-next-line
  }, [project?.id, storageKey])

  async function save() {
    setSaveError(null)
    if (!project?.id) { setSaveError('Sin proyecto activo'); return }
    const { data: u } = await supabase.auth.getUser()
    const user_id = u?.user?.id
    if (!user_id) { setSaveError('No autenticado'); return }
    const m = computeMetrics()
    const { error } = await supabase.from('esg_snapshots').insert([{
      project_id: project.id, user_id,
      framework: 'gresb',
      indicator_code: 'GRESB_REAL_ESTATE',
      indicator_name: 'GRESB Real Estate Assessment — snapshot',
      value_numeric: m.total,
      value_unit: 'pts',
      source_module: 'GRESBAssessment',
      source_data: data,
    }])
    if (error) { setSaveError(error.message); return }
    // Backup local
    try { localStorage.setItem(storageKey, JSON.stringify(data)) } catch {}
    setSavedMsg(true)
    setTimeout(() => setSavedMsg(false), 2000)
  }

  // ─── Metrics ─────────────────────────────────────────────────────────────
  function computeMetrics() {
    const assets = data.performance?.assets || []
    const assetsM2 = assets.reduce((s, a) => s + (parseFloat(a.m2) || 0), 0)
    // Si no hay activos en el portfolio pero sí sensors + ENE 1, usar m² del proyecto
    const totalM2 = assetsM2 > 0 ? assetsM2 : (fallbackM2 || 0)

    // BC1.1: m² ajustado × validación GRESB × factor tiempo (diseño/construcción)
    const bc11AdjM2 = assets.reduce((s, a) => {
      if (!a.cert_env || a.cert_env === 'Ninguna') return s
      return s + (parseFloat(a.m2) || 0) * (VALIDATION_WEIGHT[a.cert_env_validation] ?? 1.0) * bc11TimeFactor(a.cert_env_year)
    }, 0)
    // BC1.2: m² ajustado × validación GRESB × factor tiempo (operacional, caduca a 5 años)
    const bc12AdjM2 = assets.reduce((s, a) => {
      if (!a.cert_hlw || a.cert_hlw === 'Ninguna') return s
      return s + (parseFloat(a.m2) || 0) * (VALIDATION_WEIGHT[a.cert_hlw_validation] ?? 1.0) * bc12TimeFactor(a.cert_hlw_year)
    }, 0)

    let totalKwh = assets.reduce((s, a) => s + (parseFloat(a.kwh_year) || 0), 0)
    let totalCo2t = assets.reduce((s, a) => s + (parseFloat(a.tco2_scope1) || 0) + (parseFloat(a.tco2_scope2) || 0), 0)
    let totalM3 = assets.reduce((s, a) => s + (parseFloat(a.m3_year) || 0), 0)
    // Fallback a sensores agregados (building_sensors) cuando los activos no llevan KPI operacional
    let kpiSource = 'assets'
    if ((totalKwh === 0 || totalCo2t === 0 || totalM3 === 0) && sensors) {
      if (totalKwh === 0)  { totalKwh  = sensors.kwh; kpiSource = 'sensors' }
      if (totalCo2t === 0) { totalCo2t = sensors.co2 / 1000; kpiSource = 'sensors' }
      if (totalM3 === 0)   { totalM3   = sensors.m3; kpiSource = 'sensors' }
    }

    const bc11_adj_pct = totalM2 > 0 ? (bc11AdjM2 / totalM2) * 100 : 0
    const bc12_adj_pct = totalM2 > 0 ? (bc12AdjM2 / totalM2) * 100 : 0
    const en1_i = totalM2 > 0 && totalKwh > 0 ? totalKwh / totalM2 : null
    const gh1_i = totalM2 > 0 && totalCo2t > 0 ? (totalCo2t * 1000) / totalM2 : null
    const wt1_i = totalM2 > 0 && totalM3 > 0 ? totalM3 / totalM2 : null
    const ws_pct_val = parseFloat(data.performance?.ws_pct_desvio) || 0
    const ws_pct = ws_pct_val > 0 ? ws_pct_val : null

    const bc11p     = bc11Pts(bc11_adj_pct)
    const bc12p     = bc12Pts(bc12_adj_pct)
    const bc1_total = Math.min(bc11p + bc12p, 8.5)  // CAP GRESB: BC1.1+BC1.2 max 8.5 pts
    // Performance calculable = solo BC1 (certificaciones)
    // EN1/GH1/WT1/WS1 son relativos al peer group → no calculables sin benchmark GRESB
    const perfTotal = bc1_total

    let mgmtTotal = 0
    MGMT_SECTIONS.forEach(s => {
      s.items.forEach(item => {
        if (data.management?.[s.id]?.items?.[item.id] === 'si') mgmtTotal += item.pts
      })
    })
    mgmtTotal = Math.min(mgmtTotal, 30)
    const total = mgmtTotal + perfTotal

    return {
      totalM2, bc11AdjM2, bc12AdjM2, totalKwh, totalCo2t, totalM3,
      bc11_adj_pct, bc12_adj_pct, en1_i, gh1_i, wt1_i, ws_pct,
      bc11p, bc12p, bc1_total,
      perfTotal, mgmtTotal, total, assets, kpiSource,
    }
  }

  // ─── Exports ─────────────────────────────────────────────────────────────
  function exportAssetsCSV() {
    const assets = data.performance?.assets || []
    if (!assets.length) return
    downloadCSV(
      assets.map(a => ({
        nombre: a.nombre, uso: a.uso, m2: a.m2,
        kwh_year: a.kwh_year, tco2_scope1: a.tco2_scope1, tco2_scope2: a.tco2_scope2, m3_year: a.m3_year,
        cert_env: a.cert_env, cert_env_year: a.cert_env_year,
        cert_hlw: a.cert_hlw, cert_hlw_year: a.cert_hlw_year,
      })),
      `GRESB_activos_${(project?.name || 'proyecto').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`
    )
  }
  function exportReportPDF() {
    const m = computeMetrics()
    const sections = [
      { title: 'Resumen GRESB Real Estate Assessment', cols: ['Indicador', 'Valor'], rows: [
        ['Management',          `${m.mgmtTotal.toFixed(2)} / 30 pts`],
        ['Performance BC1',     `${m.bc1_total.toFixed(2)} / 8,5 pts`],
        ['Total estimado',      `${m.total.toFixed(2)} pts`],
        ['m² portfolio',        m.totalM2.toLocaleString()],
        ['EN1 intensidad',      m.en1_i != null ? `${m.en1_i.toFixed(0)} kWh/m²·año` : '—'],
        ['GH1 intensidad',      m.gh1_i != null ? `${m.gh1_i.toFixed(1)} kgCO₂e/m²·año` : '—'],
        ['WT1 intensidad',      m.wt1_i != null ? `${m.wt1_i.toFixed(2)} m³/m²·año` : '—'],
        ['Fuente KPIs operativos', m.kpiSource === 'sensors' ? `building_sensors año ${sensors?.year ?? '—'}` : 'datos por activo (manual)'],
      ]},
      { title: 'Portfolio de activos', cols: ['Nombre', 'm²', 'kWh/año', 'tCO₂e/año', 'Cert. env.', 'Cert. HLW'],
        rows: (data.performance?.assets || []).map(a => [a.nombre || '—', a.m2 || '—', a.kwh_year || '—', ((+a.tco2_scope1 || 0) + (+a.tco2_scope2 || 0)) || '—', a.cert_env || '—', a.cert_hlw || '—']) },
    ]
    openPrintWindow({ title: 'GRESB Assessment', projectName: project?.name || 'Proyecto', sections })
  }

  // ─── Handlers ────────────────────────────────────────────────────────────
  function setMgmtItem(sId, iId, val) {
    setData(d => ({
      ...d,
      management: {
        ...d.management,
        [sId]: { ...d.management[sId], items: { ...d.management[sId]?.items, [iId]: val } },
      },
    }))
  }
  function setMgmtNotes(sId, val) {
    setData(d => ({ ...d, management: { ...d.management, [sId]: { ...d.management[sId], notes: val } } }))
  }

  // Auto-cumplir preguntas RM cuando hay IROs relevantes detectados en el módulo IRO
  function autoFillRMFromIros() {
    if (!iros || iros.length === 0) { setAutofillMsg({ ok: false, text: 'No hay IROs activos en el proyecto todavía. Ejecuta el módulo Monitorización → IROs primero.' }); return }
    const has = (id) => iros.some(i => i.detector_id === id)
    const hasArpsiVerified = iroSettings?.arpsi_status === 'not_at_risk'
    // Reglas de auto-cumplimiento basadas en presencia de IROs
    const anyClimateRisk = iros.some(i => (i.category || '').includes('climate') && (i.type === 'risk' || i.type === 'impact'))
    const anyPhysicalRisk = has('D10.calor_cronico') || has('D11.riesgo_inundacion') || hasArpsiVerified
    const anyTransitionRisk = has('D7.eu_ets') || has('D8.riesgo_zbe')

    const updates = []
    if (anyClimateRisk || iros.length > 0) updates.push({ id: 'RM2', val: 'si', why: 'IROs de clima detectados por el módulo IRO' })
    if (anyPhysicalRisk) updates.push({ id: 'RM3_1', val: 'si', why: hasArpsiVerified && !has('D11.riesgo_inundacion') ? 'D11 verificado ARPSI (evaluación efectuada)' : 'D10/D11 disparados' })
    if (anyTransitionRisk) updates.push({ id: 'RM3_2', val: 'si', why: 'D7 EU ETS y/o D8 ZBE detectados en el portfolio' })
    if (anyTransitionRisk) updates.push({ id: 'RM6_1', val: 'si', why: 'Riesgos regulatorios / mercado carbono identificados por D7/D8' })

    if (updates.length === 0) { setAutofillMsg({ ok: false, text: 'Ningún IRO alimenta las preguntas RM.' }); return }

    setData(d => {
      const rmItems = { ...(d.management?.RM?.items || {}) }
      updates.forEach(u => { rmItems[u.id] = u.val })
      return { ...d, management: { ...d.management, RM: { ...(d.management?.RM || {}), items: rmItems } } }
    })
    setAutofillMsg({ ok: true, text: `Auto-completadas ${updates.length} preguntas RM basadas en los IROs detectados: ${updates.map(u => u.id).join(', ')}. Recuerda pulsar Guardar para persistir.` })
  }
  function addAsset() {
    const id = Date.now()
    setData(d => ({ ...d, performance: { ...d.performance, assets: [...(d.performance.assets || []), newAsset(id)] } }))
    setEditAssetId(id)
  }
  function updateAsset(id, field, val) {
    setData(d => ({ ...d, performance: { ...d.performance, assets: (d.performance.assets || []).map(a => a.id === id ? { ...a, [field]: val } : a) } }))
  }
  function removeAsset(id) {
    setData(d => ({ ...d, performance: { ...d.performance, assets: (d.performance.assets || []).filter(a => a.id !== id) } }))
    if (editAssetId === id) setEditAssetId(null)
  }
  function setPerf(field, val) {
    setData(d => ({ ...d, performance: { ...d.performance, [field]: val } }))
  }
  function importENE1toAsset(assetId) {
    if (!project?.id) return
    const raw = localStorage.getItem(`ene1_${project.id}`)
    if (!raw) { alert('No hay datos de ENE 1 guardados para este proyecto. Abre la calculadora ENE 1 primero.'); return }
    try {
      const { zones } = JSON.parse(raw)
      const valid = (zones || []).filter(z => parseFloat(z.m2) > 0 && parseFloat(z.con_eval) >= 0)
      if (!valid.length) { alert('La calculadora ENE 1 no tiene zonas con datos completos.'); return }
      const totalM2ene1 = valid.reduce((s, z) => s + parseFloat(z.m2), 0)
      // con_eval = consumo energético en kWh/m²·año (media ponderada por m²)
      const conEvalPond = valid.reduce((s, z) => s + parseFloat(z.con_eval) * parseFloat(z.m2), 0) / totalM2ene1
      setData(d => ({
        ...d,
        performance: {
          ...d.performance,
          assets: (d.performance.assets || []).map(a => {
            if (a.id !== assetId) return a
            const m2 = parseFloat(a.m2) > 0 ? parseFloat(a.m2) : totalM2ene1
            const kwh_year = Math.round(m2 * conEvalPond)
            const updates = { kwh_year: String(kwh_year), kwh_from_ene1: true }
            if (!parseFloat(a.m2)) updates.m2 = String(Math.round(totalM2ene1))
            return { ...a, ...updates }
          }),
        },
      }))
    } catch { alert('Error leyendo datos de ENE 1.') }
  }
  function importCurrentProject() {
    const id = Date.now()
    const a = newAsset(id)
    a.nombre = project?.name || 'Este edificio'
    a.cert_env = 'BREEAM Very Good'
    setData(d => ({ ...d, performance: { ...d.performance, assets: [...(d.performance.assets || []), a] } }))
    setEditAssetId(id)
    setActiveTab('performance')
  }

  const m = computeMetrics()

  // ─── Dashboard tab ────────────────────────────────────────────────────────
  function renderDashboard() {
    const perfIndicators = [
      { label: 'BC1 Certificaciones', pts: m.bc1_total, max: 8.5, relative: false, note: m.bc11_adj_pct > 0 ? `${m.bc11_adj_pct.toFixed(0)}% adj` : 'Sin datos' },
      { label: 'EN1 Energía',   pts: null, max: PERF_RELATIVE.EN1.max, relative: true, note: m.en1_i != null ? `${m.en1_i.toFixed(0)} kWh/m²` : '—' },
      { label: 'GH1 GEI',       pts: null, max: PERF_RELATIVE.GH1.max, relative: true, note: m.gh1_i != null ? `${m.gh1_i.toFixed(1)} kgCO₂e/m²` : '—' },
      { label: 'WT1 Agua',      pts: null, max: PERF_RELATIVE.WT1.max, relative: true, note: m.wt1_i != null ? `${m.wt1_i.toFixed(2)} m³/m²` : '—' },
      { label: 'WS1 Residuos',  pts: null, max: PERF_RELATIVE.WS1.max, relative: true, note: m.ws_pct != null ? `${m.ws_pct.toFixed(0)}%` : '—' },
    ]
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Portal notice */}
        <div style={{ ...CARD, background: '#FFF8E7', border: '1px solid #E6A817', padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Warning size={18} style={{ color: '#E6A817', flexShrink: 0 }} />
          <span style={{ fontSize: '0.85rem', color: '#5A3E00' }}>
            Portal GRESB 2026 abierto del <strong>1 abril al 1 julio 2026</strong>. Los datos aquí introducidos facilitan el volcado al cuestionario oficial.
          </span>
        </div>

        {/* Score cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={{ ...CARD, textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Management</div>
            <div style={{ fontSize: '3.5rem', fontWeight: '800', color: '#2C2017', lineHeight: 1 }}>{m.mgmtTotal}</div>
            <div style={{ fontSize: '1rem', color: '#7A6A5A', marginTop: '0.25rem' }}>/ 30 puntos</div>
            <div style={{ marginTop: '1rem', height: '8px', background: '#EEE8E0', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(m.mgmtTotal / 30) * 100}%`, background: '#5C4033', borderRadius: '4px', transition: 'width 0.4s' }} />
            </div>
          </div>
          <div style={{ ...CARD, textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>BC1 Certificaciones</div>
            <div style={{ fontSize: '3.5rem', fontWeight: '800', color: '#2C2017', lineHeight: 1 }}>{m.bc1_total.toFixed(1)}</div>
            <div style={{ fontSize: '1rem', color: '#7A6A5A', marginTop: '0.25rem' }}>/ 8.5 puntos</div>
            <div style={{ marginTop: '1rem', height: '8px', background: '#EEE8E0', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(m.bc1_total / 8.5) * 100}%`, background: '#2C6E91', borderRadius: '4px', transition: 'width 0.4s' }} />
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: '#aaa' }}>BC1.1 + BC1.2 (cap conjunto)</div>
          </div>
        </div>

        {/* Management vs Performance breakdown */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={CARD}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#2C2017' }}>Management</span>
              <span style={{ fontWeight: '700', color: '#5C4033' }}>{m.mgmtTotal} / 30</span>
            </div>
            <div style={{ height: '10px', background: '#EEE8E0', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(m.mgmtTotal / 30) * 100}%`, background: '#5C4033', borderRadius: '5px', transition: 'width 0.4s' }} />
            </div>
            {MGMT_SECTIONS.map(s => {
              const pts = s.items.reduce((sum, i) => sum + (data.management?.[s.id]?.items?.[i.id] === 'si' ? i.pts : 0), 0)
              return (
                <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                  <span style={{ color: '#7A6A5A' }}>{s.id}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '80px', height: '5px', background: '#EEE8E0', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(pts / s.maxPts) * 100}%`, background: s.color, borderRadius: '3px' }} />
                    </div>
                    <span style={{ color: '#2C2017', fontWeight: '600', width: '35px', textAlign: 'right' }}>{pts}/{s.maxPts}</span>
                  </div>
                </div>
              )
            })}
          </div>

          <div style={CARD}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#2C2017' }}>Performance</span>
              <span style={{ fontWeight: '700', color: '#5C4033' }}>{m.bc1_total.toFixed(1)}/8.5 <span style={{ fontSize: '0.72rem', color: '#aaa', fontWeight: '400' }}>+ benchmark</span></span>
            </div>
            <div style={{ height: '10px', background: '#EEE8E0', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(m.bc1_total / 8.5) * 100}%`, background: '#2C6E91', borderRadius: '5px', transition: 'width 0.4s' }} />
            </div>
            {perfIndicators.map(pi => (
              <div key={pi.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                <span style={{ color: '#7A6A5A', flex: 1 }}>{pi.label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.72rem', color: '#999' }}>{pi.note}</span>
                  {pi.relative
                    ? <span style={{ fontSize: '0.7rem', color: '#aaa', fontStyle: 'italic', width: '55px', textAlign: 'right' }}>benchmark</span>
                    : <span style={{ color: '#2C2017', fontWeight: '600', width: '35px', textAlign: 'right' }}>{pi.pts}/{pi.max}</span>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Accesos rápidos — siempre visibles */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {project && (
            <button onClick={importCurrentProject} style={BTN_PRIMARY}>
              Importar "{project?.name || 'este proyecto'}"
            </button>
          )}
          <button onClick={() => setActiveTab('performance')} style={BTN_GHOST}>
            Ir a Performance
          </button>
        </div>
      </div>
    )
  }

  // ─── Management tab ───────────────────────────────────────────────────────
  function renderManagement() {
    const iroImpact = iros.filter(i => i.type === 'impact').length
    const iroRisk = iros.filter(i => i.type === 'risk').length
    const iroOpp = iros.filter(i => i.type === 'opportunity').length
    const hasArpsiCheck = iroSettings?.arpsi_status && iroSettings.arpsi_status !== 'not_checked'

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ fontSize: '0.85rem', color: '#7A6A5A', marginBottom: '0.25rem' }}>
          El componente de Management vale <strong>30 puntos</strong>. Marca los elementos que ya dispones — usa "En proceso" para ítems pendientes.
        </div>

        {/* Banner de auto-cumplimiento RM desde IROs */}
        {(iros.length > 0 || hasArpsiCheck) && (
          <div style={{ background: '#F0F5EC', border: `1px solid #8D9A2E44`, borderLeft: `3px solid #8D9A2E`, borderRadius: 6, padding: '0.7rem 0.9rem', display: 'flex', alignItems: 'center', gap: '0.9rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: '0.78rem', color: '#2C2017', fontWeight: 600 }}>
                Módulo IRO detectó evidencia para preguntas RM
              </div>
              <div style={{ fontSize: '0.72rem', color: '#7A6A5A', marginTop: 3 }}>
                {iroImpact + iroRisk + iroOpp} IROs activos ({iroImpact} impactos · {iroRisk} riesgos · {iroOpp} oportunidades){hasArpsiCheck ? ' + verificación ARPSI' : ''}. Puedes auto-completar las preguntas RM2, RM3_1, RM3_2 y RM6_1 basándote en esta evidencia.
              </div>
            </div>
            <button onClick={autoFillRMFromIros}
              style={{ background: '#8D9A2E', color: '#FFF', border: 'none', borderRadius: 5, padding: '7px 13px', fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer' }}>
              Auto-completar RM desde IROs
            </button>
          </div>
        )}
        {autofillMsg && (
          <div style={{ background: autofillMsg.ok ? '#F0F5EC' : '#FFF1ED', border: `1px solid ${autofillMsg.ok ? '#8D9A2E' : '#D4614A'}`, borderRadius: 6, padding: '0.55rem 0.85rem', color: autofillMsg.ok ? '#2E6E3F' : '#D4614A', fontSize: '0.78rem' }}>
            {autofillMsg.text}
          </div>
        )}
        {MGMT_SECTIONS.map(section => {
          const sectionData = data.management?.[section.id] || {}
          const pts = section.items.reduce((sum, i) => sum + (sectionData.items?.[i.id] === 'si' ? i.pts : 0), 0)
          const isOpen = openSection === section.id

          return (
            <div key={section.id} style={CARD}>
              <div
                onClick={() => setOpenSection(isOpen ? null : section.id)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: section.color }} />
                  <span style={{ fontWeight: '700', fontSize: '0.95rem', color: '#2C2017' }}>{section.label}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontWeight: '700', color: pts === section.maxPts ? '#27ae60' : '#5C4033' }}>{pts}</span>
                    <span style={{ color: '#aaa' }}> / {section.maxPts} pts</span>
                  </div>
                  <span style={{ color: '#aaa', fontSize: '0.85rem' }}>{isOpen ? '▲' : '▼'}</span>
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {section.items.map(item => {
                    const val = sectionData.items?.[item.id] || 'no'
                    return (
                      <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '0.75rem', background: '#FAFAF8', borderRadius: '6px', border: '1px solid #EEE8E0' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.875rem', color: '#2C2017', fontWeight: '500', marginBottom: '0.1rem' }}>{item.label}</div>
                          <div style={{ fontSize: '0.75rem', color: '#7A6A5A', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span>{item.pts} pt{item.pts !== 1 ? 's' : ''}</span>
                            {item.tipo && <span style={{ fontSize: '0.65rem', fontWeight: '700', padding: '0.05rem 0.3rem', borderRadius: '3px', background: item.tipo === 'S' ? '#E8F0FB' : '#EBF5EE', color: item.tipo === 'S' ? '#2C6E91' : '#4A7C59' }}>{item.tipo}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.375rem', flexShrink: 0 }}>
                          {['si', 'proceso', 'no'].map(opt => {
                            const active = val === opt
                            const colors = { si: '#27ae60', proceso: '#e6a817', no: '#c0392b' }
                            const labels = { si: 'Sí', proceso: 'En proceso', no: 'No' }
                            return (
                              <button
                                key={opt}
                                onClick={() => setMgmtItem(section.id, item.id, opt)}
                                style={{
                                  padding: '0.3rem 0.6rem',
                                  border: `1.5px solid ${active ? colors[opt] : '#DDD5C8'}`,
                                  borderRadius: '5px',
                                  background: active ? colors[opt] : 'transparent',
                                  color: active ? '#FFF' : '#888',
                                  fontSize: '0.78rem',
                                  fontWeight: active ? '700' : '400',
                                  cursor: 'pointer',
                                }}
                              >
                                {labels[opt]}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}

                  <div style={{ marginTop: '0.25rem' }}>
                    <label style={LABEL_STYLE}>Notas / Evidencias</label>
                    <textarea
                      value={sectionData.notes || ''}
                      onChange={e => setMgmtNotes(section.id, e.target.value)}
                      rows={2}
                      placeholder="Documentos disponibles, estado de implementación..."
                      style={{ ...INPUT_STYLE, resize: 'vertical' }}
                    />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  // ─── Performance tab ──────────────────────────────────────────────────────
  function renderPerformance() {
    const assets = data.performance?.assets || []

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {/* Assets section */}
        <div style={CARD}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div>
              <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#2C2017' }}>Activos del Portfolio</div>
              <div style={{ fontSize: '0.8rem', color: '#7A6A5A', marginTop: '0.1rem' }}>BC1.1 / BC1.2 / EN1 / GH1 / WT1 — uno por edificio</div>
            </div>
            <button onClick={addAsset} style={{ ...BTN_GHOST, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <Plus size={14} /> Añadir activo
            </button>
          </div>

          {assets.length === 0 && (
            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#aaa', fontSize: '0.85rem' }}>
              No hay activos. Añade edificios del portfolio o importa el proyecto actual desde el Dashboard.
            </div>
          )}

          {assets.map(asset => {
            const isEditing = editAssetId === asset.id
            const totalKwhAsset = parseFloat(asset.kwh_year) || 0
            const totalM2Asset = parseFloat(asset.m2) || 0
            const en1 = totalM2Asset > 0 && totalKwhAsset > 0 ? (totalKwhAsset / totalM2Asset).toFixed(0) : null

            return (
              <div key={asset.id} style={{ border: '1px solid #DDD5C8', borderRadius: '8px', marginBottom: '0.75rem', overflow: 'hidden' }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: '#FAFAF8', cursor: 'pointer' }}
                  onClick={() => setEditAssetId(isEditing ? null : asset.id)}
                >
                  <Buildings size={16} style={{ color: '#7A6A5A', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontWeight: '600', fontSize: '0.875rem', color: '#2C2017' }}>
                    {asset.nombre || 'Sin nombre'} {asset.m2 ? `· ${Number(asset.m2).toLocaleString('es-ES')} m²` : ''}
                  </span>
                  <span style={{ fontSize: '0.78rem', color: '#aaa' }}>
                    {asset.cert_env !== 'Ninguna' ? asset.cert_env : ''}
                    {asset.cert_env_year ? ` (${asset.cert_env_year})` : ''}
                    {en1 ? ` · ${en1} kWh/m²` : ''}
                  </span>
                  <span style={{ color: '#aaa', fontSize: '0.8rem' }}>{isEditing ? '▲' : '▼'}</span>
                  <button onClick={e => { e.stopPropagation(); removeAsset(asset.id) }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', padding: '0.2rem' }}>
                    <Trash size={15} />
                  </button>
                </div>

                {isEditing && (
                  <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label style={LABEL_STYLE}>Nombre del activo</label>
                        <input value={asset.nombre} onChange={e => updateAsset(asset.id, 'nombre', e.target.value)} style={INPUT_STYLE} placeholder="Ej. Torre Diagonal, CC Bonaire..." />
                      </div>
                      <div>
                        <label style={LABEL_STYLE}>Uso</label>
                        <select value={asset.uso} onChange={e => updateAsset(asset.id, 'uso', e.target.value)} style={INPUT_STYLE}>
                          {USO_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={LABEL_STYLE}>Superficie GIA (m²)</label>
                        <input type="number" value={asset.m2} onChange={e => updateAsset(asset.id, 'm2', e.target.value)} style={INPUT_STYLE} placeholder="40000" />
                      </div>
                    </div>

                    {/* BC1.1 — Certificación medioambiental */}
                    <div style={{ background: '#FAFAF8', border: '1px solid #EEE8E0', borderRadius: '6px', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#5C4033' }}>BC1.1 — Certificación Medioambiental (max 7 pts)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: '0.5rem' }}>
                        <div>
                          <label style={LABEL_STYLE}>Esquema</label>
                          <select value={asset.cert_env} onChange={e => updateAsset(asset.id, 'cert_env', e.target.value)} style={INPUT_STYLE}>
                            {CERT_ENV_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={LABEL_STYLE}>Año cert.</label>
                          <input type="number" value={asset.cert_env_year} onChange={e => updateAsset(asset.id, 'cert_env_year', e.target.value)} style={INPUT_STYLE} placeholder="2024" min="2000" max="2026" />
                        </div>
                      </div>
                      {asset.cert_env !== 'Ninguna' && (
                        <div>
                          <label style={LABEL_STYLE}>Estado validación GRESB</label>
                          <select value={asset.cert_env_validation || 'full'} onChange={e => updateAsset(asset.id, 'cert_env_validation', e.target.value)} style={INPUT_STYLE}>
                            {VALIDATION_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          {asset.cert_env_year && (
                            <div style={{ fontSize: '0.72rem', color: '#7A6A5A', marginTop: '0.2rem' }}>
                              Factor tiempo: ×{bc11TimeFactor(asset.cert_env_year).toFixed(2)}
                              {bc11TimeFactor(asset.cert_env_year) === 0 ? ' — CADUCADA' : ''}
                            </div>
                          )}
                          <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.1rem' }}>
                            Nota: el NIVEL (Very Good, Excellent...) es solo para reporting — no afecta al score GRESB
                          </div>
                        </div>
                      )}
                    </div>

                    {/* BC1.2 — Certificación salud/bienestar */}
                    <div style={{ background: '#FAFAF8', border: '1px solid #EEE8E0', borderRadius: '6px', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#2C6E91' }}>BC1.2 — Certificación Salud/Bienestar (cap conjunto BC1: 8.5 pts)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: '0.5rem' }}>
                        <div>
                          <label style={LABEL_STYLE}>Esquema</label>
                          <select value={asset.cert_hlw} onChange={e => updateAsset(asset.id, 'cert_hlw', e.target.value)} style={INPUT_STYLE}>
                            {CERT_HLW_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={LABEL_STYLE}>Año cert.</label>
                          <input type="number" value={asset.cert_hlw_year} onChange={e => updateAsset(asset.id, 'cert_hlw_year', e.target.value)} style={INPUT_STYLE} placeholder="2024" min="2000" max="2026" />
                        </div>
                      </div>
                      {asset.cert_hlw !== 'Ninguna' && (
                        <div>
                          <label style={LABEL_STYLE}>Estado validación GRESB</label>
                          <select value={asset.cert_hlw_validation || 'full'} onChange={e => updateAsset(asset.id, 'cert_hlw_validation', e.target.value)} style={INPUT_STYLE}>
                            {VALIDATION_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          {asset.cert_hlw_year && (
                            <div style={{ fontSize: '0.72rem', color: bc12TimeFactor(asset.cert_hlw_year) === 0 ? '#c0392b' : '#7A6A5A', marginTop: '0.2rem' }}>
                              Factor tiempo: ×{bc12TimeFactor(asset.cert_hlw_year).toFixed(2)}
                              {bc12TimeFactor(asset.cert_hlw_year) === 0 ? ' — CADUCADA (≥5 años)' : ''}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <label style={{ ...LABEL_STYLE, marginBottom: 0 }}>Consumo energético (kWh/año)</label>
                          <button
                            onClick={() => importENE1toAsset(asset.id)}
                            title="Importar estimación desde calculadora ENE 1"
                            style={{ ...BTN_GHOST, padding: '0.15rem 0.5rem', fontSize: '0.72rem', border: '1px solid #C4B8A8' }}
                          >
                            ENE 1
                          </button>
                        </div>
                        <input type="number" value={asset.kwh_year} onChange={e => { updateAsset(asset.id, 'kwh_year', e.target.value); if (asset.kwh_from_ene1) updateAsset(asset.id, 'kwh_from_ene1', false) }} style={INPUT_STYLE} placeholder="7200000" />
                        {asset.kwh_from_ene1 && (
                          <div style={{ fontSize: '0.72rem', color: '#8B6A3E', marginTop: '0.2rem' }}>
                            Estimacion de diseno (ENE 1) · editable
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={LABEL_STYLE}>GEI Alcance 1 (tCO₂e/año)</label>
                        <input type="number" value={asset.tco2_scope1} onChange={e => updateAsset(asset.id, 'tco2_scope1', e.target.value)} style={INPUT_STYLE} placeholder="0" />
                      </div>
                      <div>
                        <label style={LABEL_STYLE}>GEI Alcance 2 (tCO₂e/año)</label>
                        <input type="number" value={asset.tco2_scope2} onChange={e => updateAsset(asset.id, 'tco2_scope2', e.target.value)} style={INPUT_STYLE} placeholder="840" />
                      </div>
                      <div>
                        <label style={LABEL_STYLE}>Consumo agua (m³/año)</label>
                        <input type="number" value={asset.m3_year} onChange={e => updateAsset(asset.id, 'm3_year', e.target.value)} style={INPUT_STYLE} placeholder="24000" />
                      </div>
                    </div>

                    {/* Mini metrics — valores para benchmark GRESB */}
                    {totalM2Asset > 0 && (
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        {en1 && <MetricPill label="EN1" value={`${en1} kWh/m²`} />}
                        {parseFloat(asset.m2) > 0 && (parseFloat(asset.tco2_scope1) + parseFloat(asset.tco2_scope2)) > 0 && (
                          <MetricPill label="GH1" value={`${(((parseFloat(asset.tco2_scope1) || 0) + (parseFloat(asset.tco2_scope2) || 0)) * 1000 / totalM2Asset).toFixed(1)} kgCO₂e/m²`} />
                        )}
                        {parseFloat(asset.m3_year) > 0 && (
                          <MetricPill label="WT1" value={`${(parseFloat(asset.m3_year) / totalM2Asset).toFixed(2)} m³/m²`} />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Waste - portfolio level */}
        <div style={CARD}>
          <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#2C2017', marginBottom: '0.75rem' }}>WS1 — Residuos (dato a nivel portfolio)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={LABEL_STYLE}>Total residuos generados (toneladas/año)</label>
              <input type="number" value={data.performance?.ws_total_t || ''} onChange={e => setPerf('ws_total_t', e.target.value)} style={INPUT_STYLE} placeholder="500" />
            </div>
            <div>
              <label style={LABEL_STYLE}>Tasa de desvío de vertedero (%)</label>
              <input type="number" min="0" max="100" value={data.performance?.ws_pct_desvio || ''} onChange={e => setPerf('ws_pct_desvio', e.target.value)} style={INPUT_STYLE} placeholder="75" />
              {parseFloat(data.performance?.ws_pct_desvio) > 0 && (
                <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: '#7A6A5A' }}>
                  Dato registrado — scoring relativo al benchmark GRESB (max {PERF_RELATIVE.WS1.max} pts)
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Performance score summary */}
        <div style={{ ...CARD, background: '#F5F0EB' }}>
          <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#2C2017', marginBottom: '0.75rem' }}>Resumen Performance</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
            {[
              { label: 'BC1.1', pts: m.bc11p,     max: 7,    relative: false },
              { label: 'BC1.2', pts: m.bc12p,     max: 8.5,  relative: false },
              { label: 'BC1',   pts: m.bc1_total, max: 8.5,  relative: false },
              { label: 'EN1', relative: true, value: m.en1_i != null ? `${m.en1_i.toFixed(0)} kWh/m²` : null, max: PERF_RELATIVE.EN1.max },
              { label: 'GH1', relative: true, value: m.gh1_i != null ? `${m.gh1_i.toFixed(1)} kgCO₂/m²` : null, max: PERF_RELATIVE.GH1.max },
              { label: 'WT1', relative: true, value: m.wt1_i != null ? `${m.wt1_i.toFixed(2)} m³/m²` : null, max: PERF_RELATIVE.WT1.max },
              { label: 'WS1', relative: true, value: m.ws_pct != null ? `${m.ws_pct.toFixed(0)}% desvío` : null, max: PERF_RELATIVE.WS1.max },
            ].map(pi => (
              <div key={pi.label} style={{ background: '#FFF', borderRadius: '6px', padding: '0.625rem 0.875rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.8rem', color: '#7A6A5A', fontWeight: '600', flexShrink: 0 }}>{pi.label}</span>
                {pi.relative
                  ? (pi.value
                      ? <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#2C6E91', fontFamily: "'IBM Plex Mono', monospace" }}>{pi.value}</span>
                      : <span style={{ fontSize: '0.72rem', color: '#aaa', fontStyle: 'italic' }}>sin dato</span>)
                  : <span style={{ fontSize: '0.9rem', fontWeight: '700', color: pi.pts > 0 ? '#2C6E91' : '#aaa' }}>{pi.pts}<span style={{ fontSize: '0.75rem', fontWeight: '400', color: '#aaa' }}>/{pi.max}</span></span>
                }
              </div>
            ))}
          </div>
          <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', color: '#7A6A5A' }}>BC1 calculado:</span>
            <span style={{ fontSize: '1.1rem', fontWeight: '800', color: '#2C6E91' }}>{m.bc1_total.toFixed(1)} / 8.5</span>
            <span style={{ fontSize: '0.78rem', color: '#aaa', fontStyle: 'italic' }}>EN1/GH1/WT1/WS1 por benchmark</span>
          </div>
        </div>
      </div>
    )
  }

  // ─── Informe tab ──────────────────────────────────────────────────────────
  function renderInforme() {
    const rows = [
      { label: 'Liderazgo (LE)',           cat: 'Management', pts: MGMT_SECTIONS[0].items.reduce((s, i) => s + (data.management?.LE?.items?.[i.id] === 'si' ? i.pts : 0), 0), max: MGMT_SECTIONS[0].maxPts },
      { label: 'Políticas (PO)',           cat: 'Management', pts: MGMT_SECTIONS[1].items.reduce((s, i) => s + (data.management?.PO?.items?.[i.id] === 'si' ? i.pts : 0), 0), max: MGMT_SECTIONS[1].maxPts },
      { label: 'Reporting (RP)',           cat: 'Management', pts: MGMT_SECTIONS[2].items.reduce((s, i) => s + (data.management?.RP?.items?.[i.id] === 'si' ? i.pts : 0), 0), max: MGMT_SECTIONS[2].maxPts },
      { label: 'Gestión de Riesgos (RM)', cat: 'Management', pts: MGMT_SECTIONS[3].items.reduce((s, i) => s + (data.management?.RM?.items?.[i.id] === 'si' ? i.pts : 0), 0), max: MGMT_SECTIONS[3].maxPts },
      { label: 'Stakeholder Engagement (SE)', cat: 'Management', pts: MGMT_SECTIONS[4].items.reduce((s, i) => s + (data.management?.SE?.items?.[i.id] === 'si' ? i.pts : 0), 0), max: MGMT_SECTIONS[4].maxPts },
      { label: 'BC1.1 Cert. Medioambiental (diseño)',      cat: 'Performance', pts: m.bc11p,     max: 7,                       note: m.bc11_adj_pct > 0 ? `${m.bc11_adj_pct.toFixed(0)}% m² ajustado` : 'Sin datos' },
      { label: 'BC1.2 Cert. Salud/Bienestar (operacional)', cat: 'Performance', pts: m.bc12p,    max: 8.5,                     note: m.bc12_adj_pct > 0 ? `${m.bc12_adj_pct.toFixed(0)}% m² ajustado` : 'Sin datos' },
      { label: 'BC1 total (cap conjunto 8.5 pts)',         cat: 'Performance', pts: m.bc1_total,  max: 8.5,                     note: 'Estimación lineal — definitivo por benchmark' },
      { label: 'EN1 Intensidad Energética', cat: 'Performance', pts: null, max: PERF_RELATIVE.EN1.max, relative: true, note: m.en1_i != null ? `${m.en1_i.toFixed(0)} kWh/m²` : 'Sin datos' },
      { label: 'GH1 Intensidad GEI',        cat: 'Performance', pts: null, max: PERF_RELATIVE.GH1.max, relative: true, note: m.gh1_i != null ? `${m.gh1_i.toFixed(1)} kgCO₂e/m²` : 'Sin datos' },
      { label: 'WT1 Intensidad Agua',       cat: 'Performance', pts: null, max: PERF_RELATIVE.WT1.max, relative: true, note: m.wt1_i != null ? `${m.wt1_i.toFixed(2)} m³/m²` : 'Sin datos' },
      { label: 'WS1 Desvío de Vertedero',  cat: 'Performance', pts: null, max: PERF_RELATIVE.WS1.max, relative: true, note: m.ws_pct != null ? `${m.ws_pct.toFixed(0)}%` : 'Sin datos' },
    ]

    const mgmtRows = rows.filter(r => r.cat === 'Management')
    const perfRows = rows.filter(r => r.cat === 'Performance')

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Hero */}
        <div style={{ ...CARD, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Resumen GRESB — {new Date().getFullYear()}</div>
            <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#2C2017', lineHeight: 1.1 }}>{m.mgmtTotal} <span style={{ fontSize: '1rem', color: '#aaa', fontWeight: '400' }}>/ 30 Management</span></div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#2C6E91', marginTop: '0.25rem' }}>{m.bc1_total.toFixed(1)} <span style={{ fontSize: '0.9rem', color: '#7A6A5A', fontWeight: '400' }}>/ 8.5 BC1 certificaciones</span></div>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#5C4033' }}>{m.mgmtTotal}</div>
              <div style={{ fontSize: '0.75rem', color: '#7A6A5A' }}>Management / 30</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#2C6E91' }}>{m.bc1_total.toFixed(1)}</div>
              <div style={{ fontSize: '0.75rem', color: '#7A6A5A' }}>BC1 / 8.5</div>
            </div>
          </div>
        </div>

        {/* Detail tables */}
        {[{ label: 'Management Component (30 pts)', data: mgmtRows, total: m.mgmtTotal, max: 30, color: '#5C4033' },
          { label: 'Performance — BC1 + indicadores benchmark', data: perfRows, total: m.bc1_total, max: 8.5, color: '#2C6E91', note: 'EN1/GH1/WT1/WS1 scoring relativo al peer group GRESB' }
        ].map(section => (
          <div key={section.label} style={CARD}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
              <div>
                <span style={{ fontWeight: '700', fontSize: '0.95rem', color: '#2C2017' }}>{section.label}</span>
                {section.note && <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.1rem' }}>{section.note}</div>}
              </div>
              <span style={{ fontWeight: '700', color: section.color }}>{typeof section.total === 'number' ? section.total.toFixed ? section.total.toFixed(1) : section.total : section.total} / {section.max}</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #EEE8E0' }}>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#7A6A5A', fontWeight: '600', fontSize: '0.75rem' }}>Indicador</th>
                  {section.data[0]?.note !== undefined && <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#7A6A5A', fontWeight: '600', fontSize: '0.75rem' }}>Valor</th>}
                  <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#7A6A5A', fontWeight: '600', fontSize: '0.75rem' }}>Pts obtenidos</th>
                  <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#7A6A5A', fontWeight: '600', fontSize: '0.75rem' }}>Máx</th>
                </tr>
              </thead>
              <tbody>
                {section.data.map((row, i) => (
                  <tr key={row.label} style={{ borderBottom: '1px solid #F5F0EB', background: i % 2 === 0 ? '#FFF' : '#FAFAF8' }}>
                    <td style={{ padding: '0.5rem 0.5rem', color: '#2C2017' }}>{row.label}</td>
                    {row.note !== undefined && <td style={{ padding: '0.5rem 0.5rem', color: '#7A6A5A', fontSize: '0.8rem' }}>{row.note}</td>}
                    <td style={{ padding: '0.5rem 0.5rem', textAlign: 'right', fontWeight: '700', color: row.pts > 0 ? section.color : '#ccc' }}>
                      {row.relative ? <span style={{ fontStyle: 'italic', fontWeight: '400', color: '#aaa', fontSize: '0.78rem' }}>benchmark</span> : row.pts}
                    </td>
                    <td style={{ padding: '0.5rem 0.5rem', textAlign: 'right', color: '#aaa' }}>{row.max}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {/* Gaps */}
        <div style={CARD}>
          <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#2C2017', marginBottom: '0.75rem' }}>Acciones prioritarias para mejorar score</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {rows.filter(r => !r.relative && r.pts < r.max).sort((a, b) => (b.max - b.pts) - (a.max - a.pts)).slice(0, 5).map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: '#FFF8E7', borderRadius: '6px', border: '1px solid #F5E0B0' }}>
                <span style={{ fontSize: '0.85rem', color: '#5A3E00' }}>{r.label}</span>
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#E6A817' }}>+{r.max - r.pts} pts potenciales</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...CARD, background: '#F0F5F0', border: '1px solid #C8D8C8', fontSize: '0.82rem', color: '#3A5A3A', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div><strong>Nota metodologica general:</strong> Los puntos y el rating aqui indicados son estimaciones orientativas basadas en referencias publicas de GRESB 2025. El scoring definitivo depende del peer group y del cuestionario oficial en <strong>gresb.com</strong> (portal abierto 1 abril – 1 julio 2026).</div>
          <div style={{ borderTop: '1px solid #C8D8C8', paddingTop: '0.5rem' }}>
            <strong>Nota BC1.1 / BC1.2:</strong> La ponderacion por nivel de certificacion (Outstanding ×1.0, Excellent ×0.85, Very Good ×0.65, Good ×0.40, Pass ×0.20) es una <strong>aproximacion propia</strong>, no la metodologia oficial de GRESB. Los pesos exactos no son publicos y solo estan disponibles en el scoring tool interno del portal GRESB para participantes. Usar estos valores unicamente como referencia de planificacion.
          </div>
        </div>
      </div>
    )
  }

  // ─── Main return ──────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <Medal size={22} style={{ color: '#7A6A5A' }} />
          <div>
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: '#2C2017' }}>GRESB Real Estate Assessment</h2>
            <div style={{ fontSize: '0.78rem', color: '#7A6A5A', marginTop: '0.1rem' }}>Benchmark ESG para fondos inmobiliarios · Portal 2026 abierto</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={exportAssetsCSV} disabled={!(data.performance?.assets || []).length} title="Exportar activos a CSV"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#FFFFFF', color: '#5C4A3A', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '0.4rem 0.8rem', cursor: (data.performance?.assets || []).length ? 'pointer' : 'not-allowed', opacity: (data.performance?.assets || []).length ? 1 : 0.5, fontSize: '0.8rem' }}>
            <FileCsv size={14} /> CSV
          </button>
          <button onClick={exportReportPDF} title="Generar informe PDF GRESB"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#FFFFFF', color: '#5C4A3A', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '0.4rem 0.8rem', cursor: 'pointer', fontSize: '0.8rem' }}>
            <FilePdf size={14} /> PDF
          </button>
          <button onClick={save} style={{ ...BTN_PRIMARY, display: 'flex', alignItems: 'center', gap: '0.375rem', background: savedMsg ? '#27ae60' : '#5C4033' }}>
            <FloppyDisk size={15} /> {savedMsg ? 'Guardado' : 'Guardar'}
          </button>
        </div>
      </div>
      {saveError && <div style={{ color: '#D4614A', fontSize: '0.78rem' }}>Error: {saveError}</div>}
      {sensors && <div style={{ fontSize: '0.72rem', color: '#3B7A50', background: '#F0F5EC', border: '1px solid #C8D8C0', borderRadius: '5px', padding: '0.4rem 0.7rem' }}>Sensores: usando agregado año {sensors.year} ({sensors.n} lecturas) para EN1/GH1/WT1 cuando los activos no llevan KPIs operativos.</div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #DDD5C8' }}>
        {[
          { id: 'dashboard',   label: 'Dashboard' },
          { id: 'management',  label: 'Management  30 pts' },
          { id: 'performance', label: 'Performance  70 pts' },
          { id: 'informe',     label: 'Informe' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.5rem 1.1rem',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #5C4033' : '2px solid transparent',
              marginBottom: '-2px',
              background: 'none',
              fontWeight: activeTab === tab.id ? '700' : '400',
              color: activeTab === tab.id ? '#5C4033' : '#999',
              fontSize: '0.875rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'dashboard'   && renderDashboard()}
      {activeTab === 'management'  && renderManagement()}
      {activeTab === 'performance' && renderPerformance()}
      {activeTab === 'informe'     && renderInforme()}
    </div>
  )
}

// ─── Mini helper component ────────────────────────────────────────────────────
function MetricPill({ label, value }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', padding: '0.25rem 0.625rem', background: '#F5F0EB', borderRadius: '20px', fontSize: '0.78rem' }}>
      <span style={{ color: '#7A6A5A', fontWeight: '600' }}>{label}:</span>
      <span style={{ color: '#2C2017' }}>{value}</span>
    </div>
  )
}
